"use client";

import { useCallback, useEffect, useRef, useState } from "react";

type GoogleCredentialResponse = {
  credential?: string;
};

type GoogleAccounts = {
  id: {
    initialize: (options: {
      client_id: string;
      callback: (response: GoogleCredentialResponse) => void;
      auto_select?: boolean;
      cancel_on_tap_outside?: boolean;
    }) => void;
    renderButton: (
      element: HTMLElement,
      options: {
        theme: "outline";
        size: "large";
        shape: "rectangular";
        text: "continue_with";
        width: number;
      },
    ) => void;
  };
};

declare global {
  interface Window {
    google?: { accounts: GoogleAccounts };
  }
}

export function GoogleSignIn({
  clientId,
  onCredential,
}: {
  clientId: string;
  onCredential: (credential: string) => void;
}) {
  const buttonRef = useRef<HTMLDivElement>(null);
  const [scriptReady, setScriptReady] = useState(
    () => typeof window !== "undefined" && Boolean(window.google),
  );

  const renderButton = useCallback(() => {
    if (!scriptReady || !window.google || !buttonRef.current) return;
    buttonRef.current.replaceChildren();
    window.google.accounts.id.initialize({
      client_id: clientId,
      callback: (response) => {
        if (response.credential) onCredential(response.credential);
      },
      auto_select: false,
      cancel_on_tap_outside: true,
    });
    window.google.accounts.id.renderButton(buttonRef.current, {
      theme: "outline",
      size: "large",
      shape: "rectangular",
      text: "continue_with",
      width: 320,
    });
  }, [clientId, onCredential, scriptReady]);

  useEffect(() => {
    renderButton();
  }, [renderButton]);

  useEffect(() => {
    if (window.google) return;
    const existing = document.querySelector<HTMLScriptElement>("script[data-verbalore-google-identity]");
    const script = existing || document.createElement("script");
    const markReady = () => setScriptReady(true);
    script.addEventListener("load", markReady);
    if (!existing) {
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.dataset.verbaloreGoogleIdentity = "true";
      document.head.appendChild(script);
    }
    return () => script.removeEventListener("load", markReady);
  }, []);

  return (
    <div className="google-button" ref={buttonRef} aria-label="Continue with Google" />
  );
}
